import { h } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";
import { CSSTransition } from "react-transition-group";
import { Plus, Minus } from "../../helpers/icons";

// Space (px) left visible above the accordion header once it scrolls into
// position. 0 pins the header directly beneath the sticky header/preview.
const SCROLL_OFFSET = 0;

// Nearest scrollable ancestor: on mobile this is `.monogram-modal` (the whole
// modal scrolls), on desktop it's `.monogram-modal__steps` (the steps column
// scrolls independently). Skips overflow ancestors that aren't actually
// scrollable so the walk lands on the real container at either breakpoint.
function getScrollParent(node) {
  let el = node?.parentElement;
  while (el) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

// Height (px) obscured at the top of the scroll container by elements pinned
// there. On mobile the header + live preview are `position: sticky` and sit
// above the scrolling steps; on desktop they're static/relative, so this is 0.
function stickyTopInset(container) {
  const containerTop = container.getBoundingClientRect().top;
  let inset = 0;
  container
    .querySelectorAll(".monogram-modal__header, .monogram-modal__preview")
    .forEach((el) => {
      if (getComputedStyle(el).position !== "sticky") return;
      const bottom = el.getBoundingClientRect().bottom - containerTop;
      if (bottom > inset) inset = bottom;
    });
  return inset;
}

/**
 * Shared accordion used for each step of the monogram modal (Personalization,
 * Monogram Style, Thread Color, Placement). The body opens with a height
 * animation, mirroring product-upsell-content.jsx.
 */
export default function MonogramAccordion({
  number,
  title,
  preview,
  headerImage,
  children,
  fullBleed = false,
  scrollOffset = SCROLL_OFFSET,
}) {
  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const rootRef = useRef();
  const nodeRef = useRef();
  const roRef = useRef(null);

  // Once open, bring this accordion's header to the top of the scroll area
  // (just below the sticky header/preview on mobile), leaving `scrollOffset`
  // px of prior content visible. Clamps to the end when there isn't enough
  // content below to lift the header all the way up.
  const scrollIntoPosition = () => {
    const node = rootRef.current;
    const container = node && getScrollParent(node);
    if (!container) return;
    const currentTop =
      node.getBoundingClientRect().top -
      container.getBoundingClientRect().top;
    const maxScroll = container.scrollHeight - container.clientHeight;
    const target = Math.max(
      0,
      Math.min(
        container.scrollTop + currentTop - stickyTopInset(container) -
          scrollOffset,
        maxScroll,
      ),
    );
    container.scrollTo({ top: target, behavior: "smooth" });
  };

  useEffect(() => {
    return () => roRef.current?.disconnect();
  }, []);

  const panelId = `monogram-accordion-${number}`;

  return (
    <div
      ref={rootRef}
      className={`monogram-accordion ${open ? "monogram-accordion--open" : ""} ${
        fullBleed ? "monogram-accordion--full-bleed" : ""
      }`}
    >
      <button
        type="button"
        className="monogram-accordion__header"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="monogram-accordion__lead">
          <span className="monogram-accordion__number">{number}</span>
          <span className="monogram-accordion__text">
            <span className="monogram-accordion__title">{title}</span>
            {preview && (
              <span className="monogram-accordion__preview">{preview}</span>
            )}
          </span>
        </span>
        <span className="monogram-accordion__aside">
          {headerImage && (
            <span className="monogram-accordion__image">{headerImage}</span>
          )}
          <span className="monogram-accordion__icon">
            {open ? <Minus /> : <Plus />}
          </span>
        </span>
      </button>

      <CSSTransition
        mountOnEnter={true}
        unmountOnExit={true}
        in={open}
        nodeRef={nodeRef}
        classNames="monogram-accordion__content-"
        onEntering={() => {
          if (!nodeRef.current) return;
          const inner = nodeRef.current.firstElementChild;
          if (!inner) return;
          roRef.current = new ResizeObserver(() => {
            if (nodeRef.current) setHeight(nodeRef.current.scrollHeight);
          });
          roRef.current.observe(inner);
        }}
        onEntered={() => {
          roRef.current?.disconnect();
          roRef.current = null;
          scrollIntoPosition();
        }}
        onExited={() => {
          roRef.current?.disconnect();
          roRef.current = null;
        }}
        addEndListener={(done) => {
          const node = nodeRef.current;
          if (!node) return;
          const onEnd = (event) => {
            if (event.target !== node || event.propertyName !== "height") return;
            node.removeEventListener("transitionend", onEnd);
            done();
          };
          node.addEventListener("transitionend", onEnd, false);
        }}
      >
        <div
          className="monogram-accordion__content"
          id={panelId}
          ref={nodeRef}
          style={{ "--height": `${height}px` }}
        >
          <div className="monogram-accordion__content-inner">{children}</div>
        </div>
      </CSSTransition>
    </div>
  );
}
